import { DualAxes } from '@ant-design/charts';
import { Card, Empty, Segmented } from 'antd';
import PropTypes from 'prop-types';
import { formatAnalysisAmount, formatAnalysisProfitRate } from '../utils/operation-analysis';

const METRIC_NAMES = {
  expenseRate: '费用率（%）',
  stallIncomeAmount: '档口总收入',
  storeIncomeAmount: '门店总收入（含店外档口）',
};
const INCOME_METRIC_KEYS = new Set(['storeIncomeAmount', 'stallIncomeAmount']);
const RATE_METRIC_KEYS = new Set(['expenseRate']);

const getIncomeData = (series) =>
  series
    .filter(({ metricKey, value }) => INCOME_METRIC_KEYS.has(metricKey) && Number.isFinite(value))
    .map(({ dateKey, metricKey, value }) => ({
      amount: value,
      date: dateKey,
      metricName: METRIC_NAMES[metricKey],
    }));

const getRateData = (series) =>
  series
    .filter(({ metricKey, value }) => RATE_METRIC_KEYS.has(metricKey) && Number.isFinite(value))
    .map(({ dateKey, metricKey, value }) => ({
      date: dateKey,
      metricName: METRIC_NAMES[metricKey],
      rate: value,
    }));

const formatRate = (value) => `${formatAnalysisProfitRate(value)}%`;

const OperationAnalysisTrend = ({ granularity, loading, series, onGranularityChange }) => {
  const incomeData = getIncomeData(series);
  const rateData = getRateData(series);
  const chartConfig = {
    children: [
      {
        axis: {
          y: {
            labelFormatter: formatAnalysisAmount,
          },
        },
        colorField: 'metricName',
        data: incomeData,
        tooltip: {
          items: [
            {
              channel: 'y',
              valueFormatter: formatAnalysisAmount,
            },
          ],
        },
        type: 'line',
        yField: 'amount',
      },
      {
        axis: {
          y: {
            labelFormatter: formatRate,
            position: 'right',
          },
        },
        colorField: 'metricName',
        data: rateData,
        style: {
          lineDash: [4, 4],
        },
        tooltip: {
          items: [
            {
              channel: 'y',
              valueFormatter: formatRate,
            },
          ],
        },
        type: 'line',
        yField: 'rate',
      },
    ],
    scale: {
      color: {
        domain: ['门店总收入（含店外档口）', '档口总收入', '费用率（%）'],
        range: ['#5B8FF9', '#5AD8A6', '#5D7092'],
      },
    },
    legend: {
      color: {
        layout: {
          justifyContent: 'center',
        },
        position: 'bottom',
      },
    },
  };

  return (
    <Card
      extra={
        <Segmented
          options={[
            { label: '周', value: 'week' },
            { label: '日', value: 'day' },
          ]}
          value={granularity}
          onChange={onGranularityChange}
        />
      }
      loading={loading}
      title="核心指标"
      variant="borderless"
    >
      {incomeData.length || rateData.length ? (
        <DualAxes {...chartConfig} height={320} xField="date" />
      ) : (
        <Empty description="暂无核心指标数据" />
      )}
    </Card>
  );
};

OperationAnalysisTrend.propTypes = {
  granularity: PropTypes.oneOf(['day', 'week']).isRequired,
  loading: PropTypes.bool.isRequired,
  onGranularityChange: PropTypes.func.isRequired,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      dateKey: PropTypes.string.isRequired,
      metricKey: PropTypes.oneOf(['storeIncomeAmount', 'stallIncomeAmount', 'expenseRate'])
        .isRequired,
      metricName: PropTypes.string,
      value: PropTypes.number,
    }),
  ).isRequired,
};

export default OperationAnalysisTrend;
